<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import RecoHeading from "@reco/shared/src/components/RecoHeading.vue";
import type { GridBox } from "@reco/shared/src/types/gridbox";
import type { Recommendation } from "@reco/shared/src/types/recommendation";

import type { ColorScheme } from "@reco/shared/src/constants";
import GridboxFragment from "@reco/widgets/src/components/GridboxFragment.vue";
import { usePageStore } from "@reco/widgets/src/stores/pageStore";
import { getAdjustedBundleTrackingList } from "@reco/widgets/src/utils";
import BundlesPrice from "./BundlesPrice.vue";

export type BundlesProps = {
  heading: string;
  subHeading: string;
  headingElement?: string;
  showTitle?: boolean;
  colorScheme: ColorScheme;
  recommendations: [Recommendation, Recommendation];
  recommendationItems: Recommendation[];
};
const props = defineProps<BundlesProps>();

const { t } = useI18n();

const { country, language } = usePageStore();
const currentProduct = computed(() => props.recommendations[0] as GridBox);
const recommendedProduct = computed(() => props.recommendations[1] as GridBox);
const bundlePriceHasStar = computed(() => currentProduct.value.price.hasStar || recommendedProduct.value.price.hasStar);
const totalPrice = computed(() => {
  return (currentProduct.value.price.price + recommendedProduct.value.price.price).toFixed(2);
});

const bundleTracking = computed(() => {
  const rawData = {
    collections: {
      products: {
        list: props.recommendations.map((product) => ({
          list: getAdjustedBundleTrackingList(props.recommendationItems, product),
        })),
      },
    },
  };
  return encodeURIComponent(JSON.stringify(rawData));
});

const shouldShowHeading = computed(
  () => props.showTitle !== false && (props.heading || t("reco-frontend-next.Bundles.heading")),
);
</script>

<template>
  <RecoHeading
    v-if="shouldShowHeading"
    :sub-heading="subHeading ?? t('reco-frontend-next.Bundles.subheading')"
    :heading-element="headingElement"
    :color-scheme="colorScheme"
  >
    {{ heading ?? t("reco-frontend-next.Bundles.heading") }}
  </RecoHeading>
  <!-- eslint-disable-next-line vue/no-multiple-template-root -->
  <div class="reco-bundles__main">
    <div class="reco-bundles__products">
      <div>
        <GridboxFragment
          :product="currentProduct as Recommendation"
          :recommendation-items="recommendationItems"
          disable-gridbox-link
        />
      </div>
      <div class="reco-bundles__addition ods-tile">
        <span class="reco-bundles__addition-sign" role="img" aria-label="+"></span>
      </div>
      <div>
        <GridboxFragment :product="recommendedProduct as Recommendation" :recommendation-items="recommendationItems" />
      </div>
    </div>
    <div class="reco-bundles__add-to-cart">
      <div ref="priceContainer" class="reco-bundles__total-price">
        <BundlesPrice
          :price="totalPrice"
          :has-star="bundlePriceHasStar"
          :displayed-currency="currentProduct.price.displayedCurrency"
        />
      </div>
      <div class="reco-bundles__pca-btn-wrap">
        <pca-the-button
          primary
          :erp-numbers="`[${currentProduct.erpNumber},${recommendedProduct.erpNumber}]`"
          :language="language"
          :country="country"
          :datalayerattrs="bundleTracking"
          widget-variant="redesign"
          :aria-label="
            $t('reco-frontend-next.Bundles.add_bundle_to_cart_aria_label', {
              product1Name: currentProduct.fullTitle,
              product2Name: recommendedProduct.fullTitle,
            })
          "
        >
          {{ $t("reco-frontend-next.Bundles.add_to_cart") }}
        </pca-the-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.reco-bundles {
  &__main {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    align-content: center;
    justify-content: center;

    @media (min-width: $ods-breakpoint-tablet-landscape-min) {
      flex-direction: row;
      flex-wrap: nowrap;
    }
  }

  &__products {
    display: grid;
    grid-template-columns: 1fr 0.3fr 1fr;
    padding-inline-start: 0px;
    padding: 0 10px 1rem 10px;
    margin-block-start: 0;
    margin-block-end: 0;
    border-bottom: 1px solid var(--ods-color-border-divider);

    @media (min-width: $ods-breakpoint-tablet-landscape-min) {
      flex: 0 0 60%;
      max-width: 60%;
      justify-content: center;
      padding: 0 4rem;
      border-bottom: none;
      border-right: 1px solid var(--ods-color-border-divider);
    }
  }

  &__addition {
    display: grid;
    align-items: center;
    width: 100%;
    grid-row-start: 1;
    grid-column-start: 2;

    &-sign {
      width: 1rem;
      height: 1rem;
      opacity: 0.8;
      display: block;
      fill: currentColor;
      background-size: contain;
      background-color: var(--ods-color-icon-bg-secondary);
      mask-image: url("/cdn/assets/icons/1.0.0/plus.svg");
      -webkit-mask-image: url("/cdn/assets/icons/1.0.0/plus.svg");
      margin: 0 auto;
    }

    @media (min-width: $ods-breakpoint-phone-landscape-min) {
      &-sign {
        height: 2rem;
        width: 2rem;
      }
    }
  }

  &__add-to-cart {
    align-self: center;
    display: flex;
    gap: 1rem;
    flex-direction: column;
    align-items: center;
    flex: 0 0 40%;
    grid-auto-flow: row;
    text-align: center;
    padding: 1rem 0;

    @media (min-width: $ods-breakpoint-phone-landscape-min) {
      padding: 2rem 0;
    }

    @media (min-width: $ods-breakpoint-tablet-landscape-min) {
      flex: none;
      padding: 0 4rem;
    }
  }
}

.ABTest {
  background-color: var(--ods-color-bg-default) !important;
}
</style>
